import { useEffect, useState } from 'react';
import { checkSlug, describeSlugProblem, finalizeSlug } from '@photo-merge/shared';
import { checkSlugAvailability } from '../lib/api';

export type SlugStatus =
  | { kind: 'empty' }
  | { kind: 'invalid'; message: string }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken'; suggestion?: string }
  | { kind: 'error'; message: string };

/** Long enough that typing a whole slug costs one request, short enough to feel live. */
const DEBOUNCE_MS = 350;

/**
 * Reports whether the slug the user is typing is usable.
 *
 * Works on the *finalized* slug rather than the raw field value, so a half-typed
 * "grandmas-" is checked as "grandmas" instead of being flagged as malformed for the
 * trailing hyphen the user is about to type past. Shape problems are caught locally
 * and never reach the network.
 */
export function useSlugAvailability(value: string): SlugStatus {
  const slug = finalizeSlug(value);
  const [status, setStatus] = useState<SlugStatus>({ kind: 'empty' });

  useEffect(() => {
    if (!slug) {
      setStatus({ kind: 'empty' });
      return;
    }

    const problem = checkSlug(slug);
    if (problem) {
      setStatus({ kind: 'invalid', message: describeSlugProblem(problem) });
      return;
    }

    setStatus({ kind: 'checking' });
    const controller = new AbortController();
    const timer = setTimeout(() => {
      checkSlugAvailability(slug, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setStatus(
            result.available
              ? { kind: 'available' }
              : { kind: 'taken', suggestion: result.suggestion },
          );
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setStatus({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Could not check that link',
          });
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [slug]);

  return status;
}
